/**
 * HTTP-обёртки для Jira-эндпоинтов (M13–M16).
 */
import { apiRequest } from '../../shared/api/client.ts';
import type {
  JiraPushResponse,
  JiraItemStatus,
  JobResponse,
  JiraSyncConfig,
  CreateJiraSyncConfigRequest,
  UpdateJiraSyncConfigRequest,
  MappingTemplate,
  ItemTypeFromJiraSampleDraft,
  ItemTypeFromJiraSampleCommitResponse,
  AddFieldsFromJiraPreviewResponse,
  AddFieldsFromJiraCommitResponse,
  LinkJiraIssueResponse,
  UnlinkJiraIssueResponse,
  JiraTransitionGraph,
  JiraTransitionScanResponse,
} from '@lens/shared';

export type { CreateItemLinkBody, ItemLink } from '@lens/shared';

/** Единичный push записи в Jira. */
export async function pushItemToJira(
  spaceId: string,
  itemId: string,
  opts?: { includeFieldSlugs?: string[] },
): Promise<JiraPushResponse> {
  return apiRequest<JiraPushResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-push`,
    {
      method: 'POST',
      body: opts?.includeFieldSlugs ? { includeFieldSlugs: opts.includeFieldSlugs } : undefined,
    },
  );
}

/** Получить Jira-статус записи. */
export async function getItemJiraStatus(
  spaceId: string,
  itemId: string,
): Promise<JiraItemStatus> {
  return apiRequest<JiraItemStatus>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-status`,
  );
}

/** Ручная привязка записи к существующей задаче Jira. */
export async function linkItemToJira(
  spaceId: string,
  itemId: string,
  jiraIssueKey: string,
): Promise<LinkJiraIssueResponse> {
  return apiRequest<LinkJiraIssueResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-link`,
    { method: 'POST', body: { jiraIssueKey } },
  );
}

/** Отвязать запись от задачи Jira (локально, без обращения к Jira). */
export async function unlinkItemFromJira(
  spaceId: string,
  itemId: string,
): Promise<UnlinkJiraIssueResponse> {
  return apiRequest<UnlinkJiraIssueResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-unlink`,
    { method: 'POST' },
  );
}

/**
 * Запустить bulk push. Возвращает { jobId }.
 *
 * Опционально:
 *  - includeFieldSlugs — глобальный allowlist полей (по slug Lens). Если задан,
 *    в push идут только перечисленные поля.
 *  - excludeFieldSlugsByItem — точечные исключения по itemId.
 */
type BulkPushTarget = { itemIds: string[] } | { viewId: string };
type BulkPushFilter = {
  includeFieldSlugs?: string[];
  excludeFieldSlugsByItem?: Record<string, string[]>;
};
export async function bulkJiraPush(
  spaceId: string,
  body: BulkPushTarget & BulkPushFilter,
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `/api/spaces/${spaceId}/items/bulk-jira-push`,
    { method: 'POST', body },
  );
}

/** Получить статус job'а (для polling прогресса). */
export async function getJob(jobId: string): Promise<JobResponse> {
  return apiRequest<JobResponse>(`/api/jobs/${jobId}`);
}

// ─── Sync Inbox + per-record diff + bulk pull (новый sync-флоу) ─────────

import type {
  SyncInboxResponse,
  PushDiffResponse,
  PullDiffResponse,
  BulkJiraPullRequest,
} from '@lens/shared';

/** View-level inbox: список записей со счётчиками изменений по выбранному направлению.
 *
 * Если в body передан `itemIds` — inbox ограничивается этими записями (быстрая
 * синхронизация по выбранным строкам таблицы), view-фильтры при этом не применяются.
 */
export async function fetchSyncInbox(
  spaceId: string,
  viewId: string,
  direction: 'push' | 'pull',
  body: { includeFieldSlugs?: string[]; itemIds?: string[] } = {},
): Promise<SyncInboxResponse> {
  return apiRequest<SyncInboxResponse>(
    `/api/spaces/${spaceId}/views/${viewId}/jira-sync/inbox?direction=${direction}`,
    { method: 'POST', body },
  );
}

/** Per-record drill-down (push). */
export async function fetchPushDiff(
  spaceId: string,
  itemId: string,
  body: { includeFieldSlugs?: string[] } = {},
): Promise<PushDiffResponse> {
  return apiRequest<PushDiffResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-sync/diff?direction=push`,
    { method: 'POST', body },
  );
}

/** Per-record drill-down (pull). */
export async function fetchPullDiff(
  spaceId: string,
  itemId: string,
  body: { includeFieldSlugs?: string[] } = {},
): Promise<PullDiffResponse> {
  return apiRequest<PullDiffResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-sync/diff?direction=pull`,
    { method: 'POST', body },
  );
}

/** Запустить bulk pull. */
export async function bulkJiraPull(
  spaceId: string,
  body: BulkJiraPullRequest,
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `/api/spaces/${spaceId}/items/bulk-jira-pull`,
    { method: 'POST', body },
  );
}

// ─── Links sync (отдельный режим в Sync Inbox) ──────────────────────────

import type {
  LinksInboxResponse,
  LinksPushDiffResponse,
  LinksPullDiffResponse,
  BulkLinksSyncRequest,
} from '@lens/shared';

export async function fetchLinksInbox(
  spaceId: string,
  viewId: string,
  direction: 'push' | 'pull',
  body: { includeLinkTypes?: string[]; itemIds?: string[] } = {},
): Promise<LinksInboxResponse> {
  return apiRequest<LinksInboxResponse>(
    `/api/spaces/${spaceId}/views/${viewId}/jira-sync/links-inbox?direction=${direction}`,
    { method: 'POST', body },
  );
}

export async function fetchLinksDiff(
  spaceId: string,
  itemId: string,
  direction: 'push' | 'pull',
  body: { includeLinkTypes?: string[] } = {},
): Promise<LinksPushDiffResponse | LinksPullDiffResponse> {
  return apiRequest<LinksPushDiffResponse | LinksPullDiffResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-sync/links-diff?direction=${direction}`,
    { method: 'POST', body },
  );
}

export async function bulkLinksSync(
  spaceId: string,
  body: BulkLinksSyncRequest,
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `/api/spaces/${spaceId}/items/bulk-jira-links-sync`,
    { method: 'POST', body },
  );
}

/** Синхронизация полей И связей одной операцией (scope='both'). */
export async function bulkJiraSyncBoth(
  spaceId: string,
  body: import('@lens/shared').BulkJiraSyncBothRequest,
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `/api/spaces/${spaceId}/items/bulk-jira-sync-both`,
    { method: 'POST', body },
  );
}

// ─── Sync Config CRUD ────────────────────────────────────────────────────

/** Список sync-конфигов пространства. */
export async function listSyncConfigs(spaceId: string): Promise<JiraSyncConfig[]> {
  return apiRequest<JiraSyncConfig[]>(`/api/spaces/${spaceId}/jira-sync-configs`);
}

/** Создать sync-конфиг. */
export async function createSyncConfig(
  spaceId: string,
  data: CreateJiraSyncConfigRequest,
): Promise<JiraSyncConfig> {
  return apiRequest<JiraSyncConfig>(`/api/spaces/${spaceId}/jira-sync-configs`, {
    method: 'POST',
    body: data,
  });
}

/** Обновить sync-конфиг. */
export async function updateSyncConfig(
  spaceId: string,
  itemTypeId: string,
  data: UpdateJiraSyncConfigRequest,
): Promise<JiraSyncConfig> {
  return apiRequest<JiraSyncConfig>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}`,
    { method: 'PUT', body: data },
  );
}

export interface LinkSyncRunResponse {
  processed: number;
  linksCreated: number;
  linksRemoved: number;
  skipped: number;
  errors: Array<{ itemId?: string; jiraIssueKey?: string; error: string }>;
}

/** Ручной запуск push-обновления связей (Lens → Jira issueLinks) для типа записи. */
export async function syncLinksPush(
  spaceId: string,
  itemTypeId: string,
): Promise<LinkSyncRunResponse> {
  return apiRequest<LinkSyncRunResponse>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/sync-links-push`,
    { method: 'POST' },
  );
}

export interface JiraLinksPullResponse {
  processed: number;
  linksSynced: number;
  linksRemoved: number;
  skipped: number;
  errors: Array<{ itemId?: string; jiraIssueKey?: string; error: string }>;
}

/** Перечитать issue links из Jira и обновить связи для всех записей данного itemType. */
export async function bulkPullJiraLinks(
  spaceId: string,
  itemTypeId: string,
): Promise<JiraLinksPullResponse> {
  return apiRequest<JiraLinksPullResponse>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/pull-links`,
    { method: 'POST' },
  );
}

/** Перечитать issue links из Jira и обновить связи для одной записи. */
export async function pullItemJiraLinks(
  spaceId: string,
  itemId: string,
): Promise<JiraLinksPullResponse> {
  return apiRequest<JiraLinksPullResponse>(
    `/api/spaces/${spaceId}/items/${itemId}/links/pull`,
    { method: 'POST' },
  );
}

/** Скопировать маппинг push/pull из другого itemType того же пространства. */
export async function copyMappingBetween(
  spaceId: string,
  targetItemTypeId: string,
  data: {
    sourceItemTypeId: string;
    direction: 'push' | 'pull' | 'both' | 'pushFromPull' | 'pullFromPush';
  },
): Promise<JiraSyncConfig> {
  return apiRequest<JiraSyncConfig>(
    `/api/spaces/${spaceId}/jira-sync-configs/${targetItemTypeId}/copy-from`,
    { method: 'POST', body: data },
  );
}

/** Удалить sync-конфиг. */
export async function deleteSyncConfig(
  spaceId: string,
  itemTypeId: string,
): Promise<void> {
  await apiRequest(`/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}`, {
    method: 'DELETE',
  });
}

// ─── Jira Proxy (projects / issue types / fields) ──────────────────────

export interface JiraProject {
  key: string;
  name: string;
}

export interface JiraIssueType {
  id: string;
  name: string;
  subtask: boolean;
}

export interface JiraFieldMeta {
  key: string;
  name: string;
  required: boolean;
  schema: { type: string; items?: string; custom?: string };
  allowedValues?: Array<{ id: string; value?: string; name?: string }>;
}

export interface JiraProjectStatus {
  id: string;
  name: string;
}

/** Список проектов Jira (через прокси, per-user token). */
export async function listJiraProjects(refresh = false): Promise<JiraProject[]> {
  return apiRequest<JiraProject[]>(`/api/jira/projects${refresh ? '?refresh=true' : ''}`);
}

/** Список типов задач Jira для проекта. */
export async function listJiraIssueTypes(projectKey: string): Promise<JiraIssueType[]> {
  return apiRequest<JiraIssueType[]>(`/api/jira/projects/${encodeURIComponent(projectKey)}/issue-types`);
}

/** Поля Jira для создания задачи (create meta). */
export async function listJiraFields(projectKey: string, issueTypeId: string): Promise<JiraFieldMeta[]> {
  return apiRequest<JiraFieldMeta[]>(
    `/api/jira/projects/${encodeURIComponent(projectKey)}/issue-types/${encodeURIComponent(issueTypeId)}/fields`,
  );
}

/** Workflow-статусы Jira для проекта (объединены по всем issue types). */
export async function fetchJiraProjectStatuses(projectKey: string): Promise<JiraProjectStatus[]> {
  return apiRequest<JiraProjectStatus[]>(
    `/api/jira/projects/${encodeURIComponent(projectKey)}/statuses`,
  );
}

// ─── Transition graph (многошаговая смена статуса) ──────────────────────

/** Граф workflow-переходов для диаграммы в настройке маппинга. */
export async function fetchJiraTransitionGraph(
  spaceId: string,
  itemTypeId: string,
): Promise<JiraTransitionGraph> {
  return apiRequest<JiraTransitionGraph>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/transition-graph`,
  );
}

/** Read-only скан workflow по задачам-образцам — копит рёбра графа. */
export async function scanJiraTransitionGraph(
  spaceId: string,
  itemTypeId: string,
  sampleIssueKeys: string[],
): Promise<JiraTransitionScanResponse> {
  return apiRequest<JiraTransitionScanResponse>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/transition-graph/scan`,
    { method: 'POST', body: { sampleIssueKeys } },
  );
}

/** Полный авто-скан: по задаче на каждый статус проекта (read-only). */
export async function scanAllJiraTransitionGraph(
  spaceId: string,
  itemTypeId: string,
): Promise<JiraTransitionScanResponse> {
  return apiRequest<JiraTransitionScanResponse>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/transition-graph/scan-all`,
    { method: 'POST' },
  );
}

// ─── AI Suggest Mapping ─────────────────────────────────────────────────

export interface AiMappingSuggestion {
  lensSlug: string;
  jiraKey: string;
  transform: string;
}

/** AI-подсказка маппинга полей Lens → Jira. */
export async function suggestMapping(
  lensFields: Array<{ slug: string; name: string; type: string }>,
  jiraFields: Array<{ key: string; name: string; required: boolean }>,
): Promise<AiMappingSuggestion[]> {
  const res = await apiRequest<{ mapping: AiMappingSuggestion[] }>('/api/ai/suggest-mapping', {
    method: 'POST',
    body: { lensFields, jiraFields },
  });
  return res.mapping;
}

// ─── Mapping Templates ──────────────────────────────────────────────────

/** Список шаблонов маппинга. */
export async function listMappingTemplates(): Promise<MappingTemplate[]> {
  return apiRequest<MappingTemplate[]>('/api/jira/mapping-templates');
}

// ─── Push Preview (dry-run) ───────────────────────────────────────────

/**
 * Preview push: показывает, что будет отправлено в Jira для одной записи.
 * Если передан `draft.fieldMapping` — preview применяет его вместо сохранённого конфига
 * (позволяет проверять маппинг до save).
 */
export async function pushPreview(
  spaceId: string,
  itemTypeId: string,
  itemId?: string,
  draft?: { fieldMapping: import('@lens/shared').JiraFieldMapping },
): Promise<PushPreviewResponse> {
  return apiRequest<PushPreviewResponse>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/preview`,
    { method: 'POST', body: { itemId, draft } },
  );
}

// ─── Pull from Jira (Wave 2) ──────────────────────────────────────────

import type {
  JiraPullPreviewRequest,
  JiraPullPreviewResponse,
  JiraPullExecuteRequest,
  JiraPullExecuteResponse,
  WeightValidationRequest,
  WeightValidationResponse,
  PushPreviewResponse,
} from '@lens/shared';

/** Preview pull: показывает, что будет импортировано. */
export async function pullPreview(
  spaceId: string,
  data: JiraPullPreviewRequest,
): Promise<JiraPullPreviewResponse> {
  return apiRequest<JiraPullPreviewResponse>(
    `/api/spaces/${spaceId}/jira-pull/preview`,
    { method: 'POST', body: data },
  );
}

/** Execute pull: выполняет импорт. */
export async function pullExecute(
  spaceId: string,
  data: JiraPullExecuteRequest,
): Promise<JiraPullExecuteResponse> {
  return apiRequest<JiraPullExecuteResponse>(
    `/api/spaces/${spaceId}/jira-pull/execute`,
    { method: 'POST', body: data },
  );
}

export interface JiraSnapshotField {
  jiraKey: string;
  label: string;
  value: string | null;
}

export interface JiraSnapshotResponse {
  syncConfigId: string | null;
  displayFields: string[];
  fields: JiraSnapshotField[];
  allAvailableFields: JiraSnapshotField[];
}

/** Получить снэпшот полей Jira для записи. */
export async function fetchJiraSnapshot(spaceId: string, itemId: string): Promise<JiraSnapshotResponse> {
  return apiRequest<JiraSnapshotResponse>(`/api/spaces/${spaceId}/items/${itemId}/jira-snapshot`);
}

/** Обновить Jira-статус записи (refresh). */
export async function refreshItemJiraStatus(
  spaceId: string,
  itemId: string,
): Promise<JiraItemStatus> {
  return apiRequest<JiraItemStatus>(
    `/api/spaces/${spaceId}/items/${itemId}/jira-refresh`,
    { method: 'POST' },
  );
}

/** Валидация весов целей. */
export async function validateWeights(
  spaceId: string,
  data: WeightValidationRequest,
): Promise<WeightValidationResponse> {
  return apiRequest<WeightValidationResponse>(
    `/api/spaces/${spaceId}/weight-validation`,
    { method: 'POST', body: data },
  );
}

// ─── Saved Pull Queries ─────────────────────────────────────────────────

export interface SavedPullQuery {
  id: string;
  name: string;
  jql: string;
  mode: 'create_only' | 'create_and_update';
  createdAt: string;
}

/** Список сохранённых запросов pull. */
export async function listSavedQueries(
  spaceId: string,
  itemTypeId: string,
): Promise<SavedPullQuery[]> {
  const res = await apiRequest<{ queries: SavedPullQuery[] }>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/saved-queries`,
  );
  return res.queries;
}

/** Создать сохранённый запрос. */
export async function createSavedQuery(
  spaceId: string,
  itemTypeId: string,
  data: { name: string; jql: string; mode: string },
): Promise<SavedPullQuery> {
  return apiRequest<SavedPullQuery>(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/saved-queries`,
    { method: 'POST', body: data },
  );
}

/** Удалить сохранённый запрос. */
export async function deleteSavedQuery(
  spaceId: string,
  itemTypeId: string,
  queryId: string,
): Promise<void> {
  await apiRequest(
    `/api/spaces/${spaceId}/jira-sync-configs/${itemTypeId}/saved-queries/${queryId}`,
    { method: 'DELETE' },
  );
}

// ─── Linked Aggregates ──────────────────────────────────────────────────

export interface ItemAggregate {
  itemId: string;
  goalCount: number;
  weightSum: number;
  pushedCount: number;
}

/** Получить агрегаты для списка записей (виртуальные колонки). */
export async function fetchLinkedAggregates(
  spaceId: string,
  itemIds: string[],
): Promise<ItemAggregate[]> {
  if (itemIds.length === 0) return [];
  const res = await apiRequest<{ aggregates: ItemAggregate[] }>(
    `/api/spaces/${spaceId}/linked-aggregates`,
    { method: 'POST', body: { itemIds } },
  );
  return res.aggregates;
}

/** Типы связей Jira. */
export interface JiraIssueLinkType {
  id: string;
  name: string;
  inward: string;
  outward: string;
}

export async function listIssueLinkTypes(): Promise<JiraIssueLinkType[]> {
  return apiRequest<JiraIssueLinkType[]>('/api/jira/issue-link-types');
}

// Авто-синхронизация по cron'у переехала в features/automation/api.ts
// (см. listAutomationRules / createAutomationRule / runAutomationNow / ...).

// ─── Создание ItemType из примера Jira-задачи ─────────────────────────────

export async function previewItemTypeFromJiraSample(
  spaceId: string,
  issueKeys: string[],
): Promise<ItemTypeFromJiraSampleDraft> {
  const res = await apiRequest<{ draft: ItemTypeFromJiraSampleDraft }>(
    `/api/spaces/${spaceId}/item-types/from-jira-sample/preview`,
    { method: 'POST', body: { issueKeys } },
  );
  return res.draft;
}

export async function commitItemTypeFromJiraSample(
  spaceId: string,
  draft: ItemTypeFromJiraSampleDraft,
): Promise<ItemTypeFromJiraSampleCommitResponse> {
  return apiRequest<ItemTypeFromJiraSampleCommitResponse>(
    `/api/spaces/${spaceId}/item-types/from-jira-sample/commit`,
    { method: 'POST', body: { draft } },
  );
}

// ─── Add fields from Jira (для существующего ItemType) ────────────────────

export async function previewAddFieldsFromJira(
  spaceId: string,
  itemTypeId: string,
  issueKeys: string[],
): Promise<AddFieldsFromJiraPreviewResponse> {
  return apiRequest<AddFieldsFromJiraPreviewResponse>(
    `/api/spaces/${spaceId}/item-types/${itemTypeId}/from-jira-sample/preview-add-fields`,
    { method: 'POST', body: { issueKeys } },
  );
}

export async function commitAddFieldsFromJira(
  spaceId: string,
  itemTypeId: string,
  draft: ItemTypeFromJiraSampleDraft,
): Promise<AddFieldsFromJiraCommitResponse> {
  return apiRequest<AddFieldsFromJiraCommitResponse>(
    `/api/spaces/${spaceId}/item-types/${itemTypeId}/from-jira-sample/commit-add-fields`,
    { method: 'POST', body: { draft } },
  );
}
